import React, { useMemo, useState } from 'react';
import {
  BarChart3,
  CheckCircle2,
  ClipboardCheck,
  Clock,
  FileCheck2,
  FileText,
  Filter,
  History,
  Link2,
  Pencil,
  Plus,
  Power,
  RotateCcw,
  Search,
  ShieldAlert,
  Star,
  Truck,
  UploadCloud,
  UserRound,
  Ban,
  XCircle,
} from 'lucide-react';
import {
  type CreateSupplierPayload,
  type HomologationStep,
  type Supplier,
  type SupplierApprovalDecision,
  type SupplierDocument,
  type SupplierDocumentType,
  type SupplierPixKeyType,
  type SupplierRiskLevel,
  type SupplierStatus,
  type SupplierType,
  type UpdateSupplierPayload,
} from '../api';
import {
  useAdicionarDocumentoFornecedor,
  useAtualizarFornecedor,
  useCriarFornecedor,
  useDashboardFornecedores,
  useFornecedor,
  useFornecedores,
  useRegistrarAprovacaoFornecedor,
  useRegistrarAvaliacaoFornecedor,
  useRevisarDocumentoFornecedor,
  useTiposDocumentoFornecedor,
} from '../hooks';
import { FEATURES, usePermissions } from '../../../shared/hooks/usePermissions';
import { useEstados, useMunicipios } from '../../geo/hooks';
import { BRAZILIAN_BANKS, bankByCode } from '../constants/banks';
import {
  SupplierBlockingConditionsPanel,
  SupplierBlockingSummary,
  SupplierHomologationMetadata,
  getSupplierBlockingConditions,
} from '../components/SupplierBlockingConditions';
import {
  SupplierDocumentUpload,
  documentTypeByCode,
} from '../components/SupplierDocumentUpload';
import { SupplierDocumentCard } from '../components/SupplierDocumentCard';
import {
  maskBrazilianPhone,
  maskCEP,
  maskPixKey,
  maskSupplierDocument,
  normalizeSupplierPayload,
  normalizeSupplierPortalPayload,
  shouldUseCustomBankFields,
  supplierToPortalForm,
  type SupplierFormErrors,
  validateSupplierForm,
} from '../validation/supplierForm';
import {
  formatLocalDate,
  isFutureCivilDate,
  nextCivilDateISO,
} from '../utils/dates';
import {
  supplierDocumentCodes,
  supplierUploadDocumentTypes,
} from '../utils/documents';

const statusLabels: Record<SupplierStatus, string> = {
  em_cadastro: 'Em Cadastro',
  pendente_documentacao: 'Pendente de Documentação',
  em_analise: 'Em Análise',
  em_homologacao: 'Em Homologação',
  homologado: 'Homologado',
  homologado_restricao: 'Homologado com Restrição',
  reprovado: 'Reprovado',
  suspenso: 'Suspenso',
  inativo: 'Inativo',
};

const statusClasses: Record<SupplierStatus, string> = {
  em_cadastro: 'bg-slate-100 text-slate-700',
  pendente_documentacao: 'bg-amber-100 text-amber-800',
  em_analise: 'bg-blue-100 text-blue-700',
  em_homologacao: 'bg-indigo-100 text-indigo-700',
  homologado: 'bg-emerald-100 text-emerald-700',
  homologado_restricao: 'bg-lime-100 text-lime-800',
  reprovado: 'bg-red-100 text-red-700',
  suspenso: 'bg-orange-100 text-orange-800',
  inativo: 'bg-gray-200 text-gray-700',
};

const riskLabels: Record<SupplierRiskLevel, string> = {
  baixo: 'Baixo',
  medio: 'Médio',
  alto: 'Alto',
  critico: 'Crítico',
};

const riskClasses: Record<SupplierRiskLevel, string> = {
  baixo: 'bg-emerald-50 text-emerald-700 border-emerald-200',
  medio: 'bg-amber-50 text-amber-700 border-amber-200',
  alto: 'bg-orange-50 text-orange-700 border-orange-200',
  critico: 'bg-red-50 text-red-700 border-red-200',
};

const stepLabels: Record<HomologationStep, string> = {
  analise_documental: 'Análise documental',
  validacao_fiscal: 'Validação Fiscal',
  validacao_financeira: 'Validação Financeira',
  validacao_juridica: 'Validação Jurídica',
  validacao_compliance: 'Validação Compliance',
  aprovacao_final: 'Aprovação Final',
};

const workflowSteps = Object.entries(stepLabels).map(([value, label]) => ({ value: value as HomologationStep, label }));

const supplierAuditFieldLabels: Record<string, string> = {
  supplierType: 'tipo de fornecedor',
  documentNumber: 'CPF/CNPJ',
  corporateName: 'razão social/nome',
  tradeName: 'nome fantasia',
  stateRegistration: 'inscrição estadual',
  municipalRegistration: 'inscrição municipal',
  taxRegime: 'regime fiscal',
  contactName: 'contato',
  contactEmail: 'e-mail',
  contactPhone: 'telefone',
  bankName: 'instituição bancária',
  bankCode: 'código COMPE',
  bankAgency: 'agência bancária',
  bankAccount: 'conta bancária',
  bankAccountType: 'tipo de conta',
  bankPixKeyType: 'tipo da chave PIX',
  bankPixKey: 'chave PIX',
  addressZipCode: 'CEP',
  addressStreet: 'logradouro',
  addressNumber: 'número',
  addressComplement: 'complemento',
  addressDistrict: 'bairro',
  addressCity: 'cidade',
  addressState: 'UF',
  addressCountry: 'país',
  categories: 'categorias',
  services: 'serviços',
  status: 'status',
  homologationStep: 'etapa de homologação',
  riskLevel: 'nível de risco',
  riskScore: 'score de risco',
  notes: 'observações',
  administrativeBlockReason: 'motivo do bloqueio administrativo',
};

const supplierDetailTabs = [
  { value: 'documentos', label: 'Documentos' },
  { value: 'homologacao', label: 'Homologação' },
  { value: 'avaliacoes', label: 'Avaliações' },
  { value: 'portal', label: 'Portal' },
  { value: 'historico', label: 'Histórico' },
  { value: 'integracoes', label: 'Integrações' },
] as const;

type SupplierDetailTab = typeof supplierDetailTabs[number]['value'];

const initialForm: CreateSupplierPayload = {
  supplierType: 'pessoa_juridica',
  documentNumber: '',
  corporateName: '',
  tradeName: '',
  contactName: '',
  contactEmail: '',
  contactPhone: '',
  stateRegistration: '',
  municipalRegistration: '',
  taxRegime: '',
  bankName: '',
  bankCode: '',
  bankAgency: '',
  bankAccount: '',
  bankAccountType: 'corrente',
  bankPixKeyType: undefined,
  bankPixKey: '',
  addressZipCode: '',
  addressStreet: '',
  addressNumber: '',
  addressComplement: '',
  addressDistrict: '',
  addressCity: '',
  addressState: '',
  addressCountry: 'Brasil',
  categories: [],
  services: [],
  riskLevel: 'baixo',
  riskScore: 0,
  notes: '',
};

const parseList = (value: string) =>
  value.split(',').map((item) => item.trim()).filter(Boolean);

const latestApprovalByStep = (supplier: Supplier, step: HomologationStep) =>
  supplier.approvals
    .filter((approval) => approval.step === step)
    .slice()
    .sort((left, right) => new Date(right.approvedAt).getTime() - new Date(left.approvedAt).getTime())[0];

const FornecedoresComprasPage: React.FC = () => {
  const [search, setSearch] = useState('');
  const [status, setStatus] = useState<SupplierStatus | ''>('');
  const [category, setCategory] = useState('');
  const [selectedSupplierId, setSelectedSupplierId] = useState<string | null>(null);
  const [showForm, setShowForm] = useState(true);
  const [editingSupplierId, setEditingSupplierId] = useState<string | null>(null);
  const [administrativeBlockReason, setAdministrativeBlockReason] = useState('');
  const [activeTab, setActiveTab] = useState<SupplierDetailTab>('documentos');
  const [form, setForm] = useState<CreateSupplierPayload>(initialForm);
  const [formErrors, setFormErrors] = useState<SupplierFormErrors>({});
  const [customBank, setCustomBank] = useState(false);
  const [categoriesText, setCategoriesText] = useState('Serviços de TI');
  const [servicesText, setServicesText] = useState('Sustentação, Consultoria');
  const [message, setMessage] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [documentType, setDocumentType] = useState('');
  const [documentExpiresAt, setDocumentExpiresAt] = useState('');
  const [documentIssuedAt, setDocumentIssuedAt] = useState('');
  const [documentFile, setDocumentFile] = useState<File | null>(null);
  const [reviewReason, setReviewReason] = useState('');
  const [approvalStep, setApprovalStep] = useState<HomologationStep>('analise_documental');
  const [approvalDecision, setApprovalDecision] = useState<SupplierApprovalDecision>('aprovado');
  const [approvalJustification, setApprovalJustification] = useState('');
  const [approvalValidUntil, setApprovalValidUntil] = useState('');
  const [approvalValidityError, setApprovalValidityError] = useState<string | null>(null);
  const [evaluation, setEvaluation] = useState({
    qualityScore: 4,
    deadlineScore: 4,
    serviceScore: 4,
    complianceScore: 4,
    priceScore: 4,
    slaScore: 4,
    comments: '',
  });

  const supplierParams = useMemo(() => ({ search, status, category, limit: 100 }), [search, status, category]);
  const { data, isLoading } = useFornecedores(supplierParams);
  const { data: dashboard } = useDashboardFornecedores();
  const { data: selectedDetail, isFetching: isSelectedDetailFetching } = useFornecedor(selectedSupplierId);
  const { data: documentTypes = [] } = useTiposDocumentoFornecedor();
  const estadosQuery = useEstados();
  const municipiosQuery = useMunicipios(form.addressState);
  const { can } = usePermissions();
  const canCreateSupplier = can(FEATURES.fornecedores, 'create');
  const canEditSupplier = can(FEATURES.fornecedores, 'edit');
  const createSupplier = useCriarFornecedor();
  const updateSupplier = useAtualizarFornecedor();
  const addDocument = useAdicionarDocumentoFornecedor();
  const reviewDocument = useRevisarDocumentoFornecedor();
  const addApproval = useRegistrarAprovacaoFornecedor();
  const addEvaluation = useRegistrarAvaliacaoFornecedor();

  const suppliers = data?.data ?? [];
  const selected = selectedSupplierId
    ? selectedDetail ?? suppliers.find((supplier) => supplier.id === selectedSupplierId) ?? null
    : suppliers[0] ?? null;
  const municipalities = useMemo(
    () => (municipiosQuery.data ?? []).slice().sort((a, b) => a.nome.localeCompare(b.nome, 'pt-BR')),
    [municipiosQuery.data],
  );
  const suppliersByCategory = useMemo(
    () => Object.entries(dashboard?.suppliersByCategory ?? {})
      .sort((left, right) => right[1] - left[1] || left[0].localeCompare(right[0], 'pt-BR')),
    [dashboard?.suppliersByCategory],
  );

  const clearFieldError = (key: keyof CreateSupplierPayload) => {
    setFormErrors((current) => {
      if (!current[key]) return current;
      const next = { ...current };
      delete next[key];
      return next;
    });
  };

  const setField = <K extends keyof CreateSupplierPayload>(key: K, value: CreateSupplierPayload[K]) => {
    setForm((current) => ({ ...current, [key]: value }));
    clearFieldError(key);
  };

  const resetSupplierForm = () => {
    setForm(initialForm);
    setFormErrors({});
    setCategoriesText('Serviços de TI');
    setServicesText('Sustentação, Consultoria');
    setCustomBank(false);
    setEditingSupplierId(null);
  };

  const startNewSupplier = () => {
    resetSupplierForm();
    setShowForm(true);
    requestAnimationFrame(() => document.getElementById('supplier-registration-form')?.scrollIntoView({ behavior: 'smooth' }));
  };

  const startEditingSupplier = (supplier: Supplier) => {
    const registration = supplierToPortalForm(supplier);
    setForm({
      ...registration,
      riskLevel: supplier.riskLevel,
      riskScore: supplier.riskScore,
      notes: supplier.notes ?? '',
    });
    setCategoriesText((supplier.categories ?? []).join(', '));
    setServicesText((supplier.services ?? []).join(', '));
    setCustomBank(shouldUseCustomBankFields(supplier.bankCode, supplier.bankName));
    setFormErrors({});
    setEditingSupplierId(supplier.id);
    setShowForm(true);
    requestAnimationFrame(() => document.getElementById('supplier-registration-form')?.scrollIntoView({ behavior: 'smooth' }));
  };

  const showSuccess = (text: string) => {
    setError(null);
    setMessage(text);
    setTimeout(() => setMessage(null), 5000);
  };

  const handleError = (err: unknown, fallback: string) => {
    const anyError = err as { response?: { data?: { message?: string } }; message?: string };
    setMessage(null);
    setError(anyError.response?.data?.message || anyError.message || fallback);
  };

  const handleSaveSupplier = async (event: React.FormEvent) => {
    event.preventDefault();
    const normalizedPayload = normalizeSupplierPayload({
      ...form,
      categories: parseList(categoriesText),
      services: parseList(servicesText),
      riskScore: Number(form.riskScore || 0),
    });
    const validationErrors = validateSupplierForm(normalizedPayload);
    setFormErrors(validationErrors);
    if (Object.keys(validationErrors).length > 0) {
      setMessage(null);
      setError('Corrija os campos destacados antes de salvar o fornecedor.');
      const firstInvalidField = Object.keys(validationErrors)[0];
      requestAnimationFrame(() => {
        document.querySelector<HTMLElement>(`[data-supplier-field="${firstInvalidField}"]`)?.focus();
      });
      return;
    }
    try {
      const supplier = editingSupplierId
        ? await updateSupplier.mutateAsync({
          id: editingSupplierId,
          payload: {
            ...normalizedPayload,
            ...normalizeSupplierPortalPayload(normalizedPayload),
            notes: normalizedPayload.notes ?? null,
          } satisfies UpdateSupplierPayload,
        })
        : await createSupplier.mutateAsync(normalizedPayload);
      setSelectedSupplierId(supplier.id);
      setShowForm(false);
      const wasEditing = Boolean(editingSupplierId);
      resetSupplierForm();
      showSuccess(wasEditing ? 'Cadastro do fornecedor atualizado com sucesso.' : 'Fornecedor cadastrado com sucesso.');
    } catch (err) {
      handleError(err, editingSupplierId ? 'Erro ao atualizar fornecedor.' : 'Erro ao cadastrar fornecedor.');
    }
  };

  const handleUploadDocument = async () => {
    if (!selected || !documentFile) return;
    const selectedDocumentType = documentTypeByCode(documentTypes, documentType);
    if (!selectedDocumentType) {
      setError('Selecione um tipo de documento válido.');
      return;
    }
    try {
      const supplier = await addDocument.mutateAsync({
        id: selected.id,
        file: documentFile,
        payload: {
          documentType,
          name: selectedDocumentType.name,
          expiresAt: selectedDocumentType.validityRule === 'with_expiration'
            ? documentExpiresAt || undefined
            : undefined,
          issuedAt: selectedDocumentType.validityRule === 'periodic_update'
            ? documentIssuedAt || undefined
            : undefined,
        },
      });
      setSelectedSupplierId(supplier.id);
      setDocumentFile(null);
      setDocumentExpiresAt('');
      setDocumentIssuedAt('');
      showSuccess('Documento enviado e versionado com sucesso.');
    } catch (err) {
      handleError(err, 'Erro ao enviar documento.');
    }
  };

  const handleReviewDocument = async (document: SupplierDocument, status: 'aprovado' | 'reprovado') => {
    if (!selected) return;
    try {
      const supplier = await reviewDocument.mutateAsync({
        supplierId: selected.id,
        documentId: document.id,
        payload: { status, rejectionReason: status === 'reprovado' ? reviewReason : undefined },
      });
      setSelectedSupplierId(supplier.id);
      setReviewReason('');
      showSuccess(status === 'aprovado' ? 'Documento aprovado.' : 'Documento reprovado.');
    } catch (err) {
      handleError(err, 'Erro ao revisar documento.');
    }
  };

  const handleApproval = async () => {
    if (!selected) return;
    const requiresValidity = approvalStep === 'aprovacao_final' && approvalDecision !== 'reprovado';
    if (requiresValidity && !approvalValidUntil) {
      const validationMessage = 'Informe a validade da homologação para concluir a aprovação final.';
      setApprovalValidityError(validationMessage);
      setMessage(null);
      setError(validationMessage);
      return;
    }
    if (requiresValidity && !isFutureCivilDate(approvalValidUntil)) {
      const validationMessage = 'A validade da homologação deve ser uma data futura.';
      setApprovalValidityError(validationMessage);
      setMessage(null);
      setError(validationMessage);
      return;
    }
    setApprovalValidityError(null);
    try {
      const supplier = await addApproval.mutateAsync({
        id: selected.id,
        payload: {
          step: approvalStep,
          decision: approvalDecision,
          justification: approvalJustification || undefined,
          validUntil: requiresValidity ? approvalValidUntil : undefined,
        },
      });
      setSelectedSupplierId(supplier.id);
      setApprovalJustification('');
      setApprovalValidUntil('');
      setApprovalValidityError(null);
      showSuccess('Homologação registrada com rastreabilidade.');
    } catch (err) {
      handleError(err, 'Erro ao registrar homologação.');
    }
  };

  const handleEvaluation = async () => {
    if (!selected) return;
    try {
      const supplier = await addEvaluation.mutateAsync({ id: selected.id, payload: evaluation });
      setSelectedSupplierId(supplier.id);
      showSuccess('Avaliação registrada com sucesso.');
    } catch (err) {
      handleError(err, 'Erro ao registrar avaliação.');
    }
  };

  const handleAdministrativeAction = async (
    action: 'suspend' | 'remove-suspension' | 'inactivate' | 'reactivate',
  ) => {
    if (!selected) return;
    if (action === 'suspend' && !administrativeBlockReason.trim()) {
      setMessage(null);
      setError('Informe o motivo obrigatório da suspensão administrativa.');
      return;
    }

    const payload: UpdateSupplierPayload = action === 'suspend'
      ? {
        status: 'suspenso',
        administrativeBlockReason: administrativeBlockReason.trim(),
      }
      : action === 'inactivate'
        ? { status: 'inativo' }
        : {
          status: 'em_cadastro',
          administrativeBlockReason: null,
        };
    const successMessage = {
      suspend: 'Fornecedor suspenso e causa registrada.',
      'remove-suspension': 'Suspensão administrativa removida e status recalculado.',
      inactivate: 'Fornecedor inativado com histórico preservado.',
      reactivate: 'Fornecedor reativado e status recalculado.',
    }[action];

    try {
      const supplier = await updateSupplier.mutateAsync({ id: selected.id, payload });
      setSelectedSupplierId(supplier.id);
      setAdministrativeBlockReason('');
      showSuccess(successMessage);
    } catch (err) {
      handleError(err, 'Erro ao atualizar o status administrativo do fornecedor.');
    }
  };

  return (
    <div className="p-6 space-y-6">
      <div className="flex flex-col gap-4 lg:flex-row lg:items-center lg:justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Compras · Fornecedores</h1>
          <p className="text-gray-600 mt-1">Cadastro, gestão documental, homologação e riscos de fornecedores.</p>
        </div>
        {canCreateSupplier && (
          <button
            type="button"
            onClick={startNewSupplier}
            className="inline-flex items-center justify-center gap-2 rounded-lg bg-blue-600 px-4 py-2 text-sm font-semibold text-white hover:bg-blue-700"
          >
            <Plus className="h-4 w-4" />
            Novo Fornecedor
          </button>
        )}
      </div>

      {message && <div role="status" aria-live="polite" className="rounded-lg border border-emerald-200 bg-emerald-50 px-4 py-3 text-sm text-emerald-700">{message}</div>}
      {error && <div role="alert" className="rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">{error}</div>}

      <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-4">
        <MetricCard icon={<Truck />} label="Ativos" value={dashboard?.activeSuppliers ?? 0} />
        <MetricCard icon={<CheckCircle2 />} label="Homologados" value={dashboard?.homologatedSuppliers ?? 0} />
        <MetricCard icon={<Clock />} label="Pendentes" value={dashboard?.pendingSuppliers ?? 0} />
        <MetricCard icon={<ShieldAlert />} label="Certidões vencidas" value={dashboard?.expiredCertificates ?? 0} tone="danger" />
        <MetricCard icon={<Clock />} label="Certidões a vencer" value={dashboard?.expiringCertificates ?? 0} />
        <MetricCard icon={<ShieldAlert />} label="Fornecedores bloqueados" value={dashboard?.blockedSuppliers ?? 0} tone="danger" testId="blocked-suppliers-metric" />
        <MetricCard icon={<BarChart3 />} label="Tempo médio de homologação" value={`${(dashboard?.avgHomologationTimeDays ?? 0).toFixed(1)} dias`} />
        <MetricCard icon={<Star />} label="Avaliação média" value={(dashboard?.averageSupplierRating ?? 0).toFixed(1)} />
      </div>

      <section aria-labelledby="suppliers-by-category-title" className="rounded-lg border border-gray-200 bg-white p-4 shadow-sm">
        <div className="flex items-center gap-2">
          <BarChart3 aria-hidden="true" className="h-5 w-5 text-blue-600" />
          <h2 id="suppliers-by-category-title" className="font-semibold text-gray-900">Fornecedores por categoria</h2>
        </div>
        {suppliersByCategory.length > 0 ? (
          <dl className="mt-3 grid gap-2 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
            {suppliersByCategory.map(([categoryName, count]) => (
              <div key={categoryName} className="flex items-center justify-between gap-3 rounded-lg border border-gray-200 px-3 py-2">
                <dt className="truncate text-sm text-gray-700" title={categoryName}>{categoryName}</dt>
                <dd className="rounded-full bg-blue-50 px-2 py-0.5 text-sm font-semibold text-blue-700">{count}</dd>
              </div>
            ))}
          </dl>
        ) : (
          <p className="mt-3 text-sm text-gray-500">Nenhum fornecedor categorizado.</p>
        )}
      </section>

      {showForm && (editingSupplierId ? canEditSupplier : canCreateSupplier) && (
        <form id="supplier-registration-form" noValidate onSubmit={handleSaveSupplier} className="rounded-lg border border-gray-200 bg-white p-5 shadow-sm">
          <div className="mb-4 flex items-center gap-2">
            <ClipboardCheck className="h-5 w-5 text-blue-600" />
            <h2 className="text-lg font-semibold text-gray-900">{editingSupplierId ? 'Editar cadastro do fornecedor' : 'Cadastro de Fornecedor'}</h2>
          </div>

          <fieldset>
            <legend className="mb-3 text-sm font-semibold text-gray-900">Identificação e contato</legend>
            <div className="grid gap-4 lg:grid-cols-4">
            <Select field="supplierType" label="Tipo" value={form.supplierType} onChange={(value) => {
              setField('supplierType', value as SupplierType);
              setField('documentNumber', '');
            }}>
              <option value="pessoa_juridica">Pessoa Jurídica</option>
              <option value="pessoa_fisica">Pessoa Física</option>
            </Select>
            <Input field="documentNumber" label={form.supplierType === 'pessoa_juridica' ? 'CNPJ' : 'CPF'} value={form.documentNumber} onChange={(value) => setField('documentNumber', maskSupplierDocument(value, form.supplierType))} error={formErrors.documentNumber} inputMode={form.supplierType === 'pessoa_juridica' ? 'text' : 'numeric'} maxLength={form.supplierType === 'pessoa_juridica' ? 18 : 14} required />
            <Input field="corporateName" label="Razão Social / Nome" value={form.corporateName} onChange={(value) => setField('corporateName', value)} error={formErrors.corporateName} maxLength={180} required />
            <Input field="tradeName" label="Nome Fantasia" value={form.tradeName || ''} onChange={(value) => setField('tradeName', value)} error={formErrors.tradeName} maxLength={180} />
            <Input field="contactName" label="Contato" value={form.contactName} onChange={(value) => setField('contactName', value)} error={formErrors.contactName} maxLength={100} required />
            <Input field="contactEmail" label="E-mail" type="email" value={form.contactEmail} onChange={(value) => setField('contactEmail', value)} error={formErrors.contactEmail} inputMode="email" maxLength={254} required />
            <Input field="contactPhone" label="Telefone" value={form.contactPhone} onChange={(value) => setField('contactPhone', maskBrazilianPhone(value))} error={formErrors.contactPhone} inputMode="tel" maxLength={15} required />
            <Select field="riskLevel" label="Risco" value={form.riskLevel || 'baixo'} onChange={(value) => setField('riskLevel', value as SupplierRiskLevel)}>
              <option value="baixo">Baixo</option>
              <option value="medio">Médio</option>
              <option value="alto">Alto</option>
              <option value="critico">Crítico</option>
            </Select>
            <Input field="stateRegistration" label="Inscrição Estadual" value={form.stateRegistration || ''} onChange={(value) => setField('stateRegistration', value)} error={formErrors.stateRegistration} maxLength={30} />
            <Input field="municipalRegistration" label="Inscrição Municipal" value={form.municipalRegistration || ''} onChange={(value) => setField('municipalRegistration', value)} error={formErrors.municipalRegistration} maxLength={30} />
            <Input field="taxRegime" label="Regime Fiscal" value={form.taxRegime || ''} onChange={(value) => setField('taxRegime', value)} error={formErrors.taxRegime} maxLength={60} />
            <Input field="riskScore" label="Score de Risco" type="number" value={String(form.riskScore ?? 0)} onChange={(value) => setField('riskScore', Number(value))} error={formErrors.riskScore} inputMode="numeric" min={0} max={100} />
            </div>
          </fieldset>

          <fieldset className="mt-6 border-t border-gray-200 pt-5">
            <legend className="px-2 text-sm font-semibold text-gray-900">Dados bancários</legend>
            <div className="mt-3 grid gap-4 lg:grid-cols-4">
              <Select field="bankCode" label="Banco" value={customBank ? '__custom__' : form.bankCode || ''} error={formErrors.bankCode} onChange={(value) => {
                if (value === '__custom__') {
                  setCustomBank(true);
                  setField('bankCode', '');
                  setField('bankName', '');
                  return;
                }
                const bank = bankByCode(value);
                setCustomBank(false);
                setField('bankCode', value || undefined);
                setField('bankName', bank?.name || undefined);
              }}>
                <option value="">Selecione pelo código COMPE</option>
                {BRAZILIAN_BANKS.map((bank) => <option key={bank.code} value={bank.code}>{bank.code} – {bank.name}</option>)}
                <option value="__custom__">Outro código COMPE</option>
              </Select>
              {customBank && (
                <>
                  <Input field="bankCode" label="Código COMPE" value={form.bankCode || ''} onChange={(value) => setField('bankCode', value.replace(/\D/g, '').slice(0, 3))} error={formErrors.bankCode} inputMode="numeric" maxLength={3} placeholder="Ex.: 041" />
                  <Input field="bankName" label="Instituição bancária" value={form.bankName || ''} onChange={(value) => setField('bankName', value)} error={formErrors.bankName} maxLength={120} />
                </>
              )}
              <Input field="bankAgency" label="Agência" value={form.bankAgency || ''} onChange={(value) => setField('bankAgency', value.toUpperCase().replace(/[^0-9A-Z-]/g, '').slice(0, 20))} error={formErrors.bankAgency} maxLength={20} />
              <Input field="bankAccount" label="Conta" value={form.bankAccount || ''} onChange={(value) => setField('bankAccount', value.toUpperCase().replace(/[^0-9A-Z-]/g, '').slice(0, 30))} error={formErrors.bankAccount} maxLength={30} />
              <Select field="bankAccountType" label="Tipo de conta" value={form.bankAccountType || ''} error={formErrors.bankAccountType} onChange={(value) => setField('bankAccountType', value || undefined)}>
                <option value="">Selecione</option>
                <option value="corrente">Conta corrente</option>
                <option value="poupanca">Conta poupança</option>
                <option value="pagamento">Conta de pagamento</option>
              </Select>
              <Select field="bankPixKeyType" label="Tipo de chave PIX" value={form.bankPixKeyType || ''} error={formErrors.bankPixKeyType} onChange={(value) => {
                setField('bankPixKeyType', (value || undefined) as SupplierPixKeyType | undefined);
                setField('bankPixKey', '');
              }}>
                <option value="">Selecione</option>
                <option value="cpf">CPF</option>
                <option value="cnpj">CNPJ</option>
                <option value="email">E-mail</option>
                <option value="phone">Telefone</option>
                <option value="random">Chave aleatória</option>
              </Select>
              <Input field="bankPixKey" label="Chave PIX" value={form.bankPixKey || ''} onChange={(value) => setField('bankPixKey', maskPixKey(value, form.bankPixKeyType))} error={formErrors.bankPixKey} inputMode={form.bankPixKeyType === 'email' ? 'email' : form.bankPixKeyType === 'phone' || form.bankPixKeyType === 'cpf' ? 'tel' : 'text'} maxLength={form.bankPixKeyType === 'email' ? 254 : form.bankPixKeyType === 'cnpj' ? 18 : form.bankPixKeyType === 'cpf' ? 14 : form.bankPixKeyType === 'phone' ? 15 : 36} disabled={!form.bankPixKeyType} />
            </div>
          </fieldset>

          <fieldset className="mt-6 border-t border-gray-200 pt-5">
            <legend className="px-2 text-sm font-semibold text-gray-900">Endereço e fornecimento</legend>
            <div className="mt-3 grid gap-4 lg:grid-cols-4">
              <Input field="addressZipCode" label="CEP" value={form.addressZipCode || ''} onChange={(value) => setField('addressZipCode', maskCEP(value))} error={formErrors.addressZipCode} inputMode="numeric" maxLength={9} />
              <Input field="addressStreet" label="Logradouro" value={form.addressStreet || ''} onChange={(value) => setField('addressStreet', value)} error={formErrors.addressStreet} maxLength={180} />
              <Input field="addressNumber" label="Número" value={form.addressNumber || ''} onChange={(value) => setField('addressNumber', value)} error={formErrors.addressNumber} maxLength={20} placeholder="Ex.: 123-A ou S/N" />
              <Input field="addressComplement" label="Complemento" value={form.addressComplement || ''} onChange={(value) => setField('addressComplement', value)} error={formErrors.addressComplement} maxLength={120} />
              <Input field="addressDistrict" label="Bairro" value={form.addressDistrict || ''} onChange={(value) => setField('addressDistrict', value)} error={formErrors.addressDistrict} maxLength={100} />
              <Select field="addressState" label="UF" value={form.addressState || ''} error={formErrors.addressState} disabled={estadosQuery.isLoading} onChange={(value) => {
                setField('addressState', value || undefined);
                setField('addressCity', '');
              }}>
                <option value="">Selecione a UF</option>
                {(estadosQuery.data ?? []).map((state) => <option key={state.uf} value={state.uf}>{state.uf} – {state.nome}</option>)}
              </Select>
              {municipiosQuery.isError ? (
                <Input field="addressCity" label="Cidade" value={form.addressCity || ''} onChange={(value) => setField('addressCity', value)} error={formErrors.addressCity} maxLength={100} />
              ) : (
                <Select field="addressCity" label="Cidade" value={form.addressCity || ''} error={formErrors.addressCity} disabled={!form.addressState || municipiosQuery.isLoading} onChange={(value) => setField('addressCity', value || undefined)}>
                  <option value="">{form.addressState ? 'Selecione a cidade' : 'Selecione primeiro a UF'}</option>
                  {municipalities.map((city) => <option key={city.idIbge} value={city.nome}>{city.nome}</option>)}
                </Select>
              )}
              <Input field="categories" label="Categorias" value={categoriesText} onChange={(value) => {
                setCategoriesText(value);
                clearFieldError('categories');
              }} error={formErrors.categories} maxLength={500} />
              <Input field="services" label="Serviços prestados" value={servicesText} onChange={(value) => {
                setServicesText(value);
                clearFieldError('services');
              }} error={formErrors.services} maxLength={1000} />
            </div>
          </fieldset>

          <div className="mt-4">
            <label htmlFor="supplier-notes" className="mb-1 block text-sm font-medium text-gray-700">Observações</label>
            <textarea
              id="supplier-notes"
              data-supplier-field="notes"
              value={form.notes || ''}
              onChange={(event) => setField('notes', event.target.value)}
              maxLength={1000}
              aria-invalid={Boolean(formErrors.notes)}
              aria-describedby={formErrors.notes ? 'supplier-notes-error' : undefined}
              className={`h-20 w-full rounded-lg border px-3 py-2 text-sm focus:outline-none focus:ring-1 ${formErrors.notes ? 'border-red-400 focus:border-red-500 focus:ring-red-500' : 'border-gray-300 focus:border-blue-500 focus:ring-blue-500'}`}
            />
            {formErrors.notes && <p id="supplier-notes-error" className="mt-1 text-xs text-red-600">{formErrors.notes}</p>}
          </div>

          <div className="mt-4 flex justify-end gap-2">
            {editingSupplierId && (
              <button
                type="button"
                onClick={() => {
                  resetSupplierForm();
                  setShowForm(false);
                }}
                className="rounded-lg border border-gray-300 px-5 py-2 text-sm font-semibold text-gray-700 hover:bg-gray-50"
              >
                Cancelar edição
              </button>
            )}
            <button
              type="submit"
              disabled={createSupplier.isPending || updateSupplier.isPending}
              className="rounded-lg bg-blue-600 px-5 py-2 text-sm font-semibold text-white hover:bg-blue-700 disabled:opacity-60"
            >
              {createSupplier.isPending || updateSupplier.isPending
                ? 'Salvando...'
                : editingSupplierId
                  ? 'Salvar alterações'
                  : 'Salvar Fornecedor'}
            </button>
          </div>
        </form>
      )}

      <div className="grid gap-6 xl:grid-cols-[minmax(520px,1fr)_460px]">
        <section className="rounded-lg border border-gray-200 bg-white shadow-sm">
          <div className="flex flex-col gap-3 border-b border-gray-200 p-4 lg:flex-row lg:items-center lg:justify-between">
            <div className="flex items-center gap-2">
              <Truck className="h-5 w-5 text-blue-600" />
              <h2 className="font-semibold text-gray-900">Base de fornecedores</h2>
            </div>
            <div className="grid gap-2 sm:grid-cols-3">
              <div className="relative">
                <Search className="absolute left-3 top-2.5 h-4 w-4 text-gray-400" />
                <input
                  value={search}
                  onChange={(event) => setSearch(event.target.value)}
                  placeholder="Buscar"
                  aria-label="Buscar fornecedores"
                  className="w-full rounded-lg border border-gray-300 py-2 pl-9 pr-3 text-sm focus:border-blue-500 focus:outline-none"
                />
              </div>
              <select aria-label="Filtrar fornecedores por status" value={status} onChange={(event) => setStatus(event.target.value as SupplierStatus | '')} className="rounded-lg border border-gray-300 px-3 py-2 text-sm">
                <option value="">Todos os status</option>
                {Object.entries(statusLabels).map(([value, label]) => <option key={value} value={value}>{label}</option>)}
              </select>
              <div className="relative">
                <Filter className="absolute left-3 top-2.5 h-4 w-4 text-gray-400" />
                <input
                  value={category}
                  onChange={(event) => setCategory(event.target.value)}
                  placeholder="Categoria"
                  aria-label="Filtrar fornecedores por categoria"
                  className="w-full rounded-lg border border-gray-300 py-2 pl-9 pr-3 text-sm focus:border-blue-500 focus:outline-none"
                />
              </div>
            </div>
          </div>

          {isLoading ? (
            <div className="p-6 text-sm text-gray-500">Carregando fornecedores...</div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full min-w-[880px]">
                <thead className="border-b border-gray-200 bg-gray-50">
                  <tr>
                    <Th>Fornecedor</Th>
                    <Th>Status</Th>
                    <Th>Risco</Th>
                    <Th>Documentos</Th>
                    <Th>Compra</Th>
                    <Th>Avaliação</Th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200">
                  {suppliers.map((supplier) => (
                    <tr
                      key={supplier.id}
                      className={`hover:bg-blue-50 ${selected?.id === supplier.id ? 'bg-blue-50' : ''}`}
                    >
                      <td className="px-4 py-3">
                        <button
                          type="button"
                          onClick={() => {
                            setSelectedSupplierId(supplier.id);
                            setAdministrativeBlockReason('');
                          }}
                          aria-controls="supplier-details"
                          aria-expanded={selected?.id === supplier.id}
                          className="rounded text-left focus:outline-none focus:ring-2 focus:ring-blue-500"
                        >
                          <span className="block font-medium text-gray-900">{supplier.tradeName || supplier.corporateName}</span>
                          <span className="block text-xs text-gray-500">{supplier.documentNumber}</span>
                        </button>
                      </td>
                      <td className="px-4 py-3"><Badge className={statusClasses[supplier.status]}>{statusLabels[supplier.status]}</Badge></td>
                      <td className="px-4 py-3"><Badge className={`border ${riskClasses[supplier.riskLevel]}`}>{riskLabels[supplier.riskLevel]} · {supplier.riskScore}</Badge></td>
                      <td className="px-4 py-3 text-sm text-gray-700">{supplier.documentSummary.total} docs · {supplier.documentSummary.expired} vencidos</td>
                      <td className="px-4 py-3"><SupplierBlockingSummary supplier={supplier} /></td>
                      <td className="px-4 py-3 text-sm text-gray-700">{Number(supplier.averageRating || 0).toFixed(1)}</td>
                    </tr>
                  ))}
                  {suppliers.length === 0 && (
                    <tr><td className="px-4 py-6 text-sm text-gray-500" colSpan={6}>Nenhum fornecedor encontrado.</td></tr>
                  )}
                </tbody>
              </table>
            </div>
          )}
        </section>

        <aside
          id="supplier-details"
          aria-live="polite"
          aria-busy={Boolean(selectedSupplierId && isSelectedDetailFetching)}
          className="rounded-lg border border-gray-200 bg-white shadow-sm"
        >
          {selected ? (
            <div>
              <div className="border-b border-gray-200 p-4">
                <div className="flex items-start justify-between gap-3">
                  <div>
                    <h2 className="text-lg font-semibold text-gray-900">{selected.tradeName || selected.corporateName}</h2>
                    <p className="text-sm text-gray-500">{selected.contactName} · {selected.contactEmail}</p>
                  </div>
                  <Badge className={statusClasses[selected.status]}>{statusLabels[selected.status]}</Badge>
                </div>
                <div className="mt-3 grid grid-cols-3 gap-2 text-center">
                  <SummaryMini label="Docs" value={selected.documentSummary.total} />
                  <SummaryMini label="Pendências" value={selected.pendingRequiredDocuments.length} />
                  <SummaryMini label="Nota" value={Number(selected.averageRating || 0).toFixed(1)} />
                </div>
                <div className="mt-3"><SupplierBlockingConditionsPanel supplier={selected} showEligible /></div>
                {canEditSupplier && (
                  <section aria-label="Ações administrativas do fornecedor" className="mt-3 space-y-3 rounded-lg border border-gray-200 bg-gray-50 p-3">
                    <div className="flex flex-wrap gap-2">
                      <button
                        type="button"
                        onClick={() => startEditingSupplier(selected)}
                        disabled={updateSupplier.isPending}
                        className="inline-flex items-center gap-1.5 rounded-lg border border-blue-200 bg-white px-3 py-2 text-xs font-semibold text-blue-700 hover:bg-blue-50 disabled:opacity-60"
                      >
                        <Pencil className="h-3.5 w-3.5" />
                        Editar cadastro
                      </button>
                      {selected.status === 'inativo' ? (
                        <button
                          type="button"
                          onClick={() => handleAdministrativeAction('reactivate')}
                          disabled={updateSupplier.isPending}
                          className="inline-flex items-center gap-1.5 rounded-lg border border-emerald-200 bg-white px-3 py-2 text-xs font-semibold text-emerald-700 hover:bg-emerald-50 disabled:opacity-60"
                        >
                          <RotateCcw className="h-3.5 w-3.5" />
                          Reativar fornecedor
                        </button>
                      ) : (
                        <button
                          type="button"
                          onClick={() => handleAdministrativeAction('inactivate')}
                          disabled={updateSupplier.isPending}
                          className="inline-flex items-center gap-1.5 rounded-lg border border-gray-300 bg-white px-3 py-2 text-xs font-semibold text-gray-700 hover:bg-gray-100 disabled:opacity-60"
                        >
                          <Power className="h-3.5 w-3.5" />
                          Inativar fornecedor
                        </button>
                      )}
                      {selected.administrativeBlockReason && selected.status !== 'inativo' && (
                        <button
                          type="button"
                          onClick={() => handleAdministrativeAction('remove-suspension')}
                          disabled={updateSupplier.isPending}
                          className="inline-flex items-center gap-1.5 rounded-lg border border-emerald-200 bg-white px-3 py-2 text-xs font-semibold text-emerald-700 hover:bg-emerald-50 disabled:opacity-60"
                        >
                          <RotateCcw className="h-3.5 w-3.5" />
                          Remover suspensão
                        </button>
                      )}
                    </div>
                    {!selected.administrativeBlockReason && selected.status !== 'inativo' && (
                      <div className="flex flex-col gap-2 sm:flex-row sm:items-end">
                        <label className="flex-1 text-xs font-medium text-gray-700">
                          Motivo da suspensão <span className="text-red-500">*</span>
                          <input
                            value={administrativeBlockReason}
                            onChange={(event) => setAdministrativeBlockReason(event.target.value)}
                            maxLength={1000}
                            placeholder="Informe a causa administrativa"
                            className="mt-1 w-full rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm focus:border-orange-500 focus:outline-none"
                          />
                        </label>
                        <button
                          type="button"
                          onClick={() => handleAdministrativeAction('suspend')}
                          disabled={updateSupplier.isPending}
                          className="inline-flex items-center justify-center gap-1.5 rounded-lg bg-orange-600 px-3 py-2 text-xs font-semibold text-white hover:bg-orange-700 disabled:opacity-60"
                        >
                          <Ban className="h-3.5 w-3.5" />
                          Suspender fornecedor
                        </button>
                      </div>
                    )}
                  </section>
                )}
              </div>

              <div role="tablist" aria-label="Detalhes do fornecedor" className="flex overflow-x-auto border-b border-gray-200 px-2">
                {supplierDetailTabs.map(({ value: tab, label }, index) => (
                  <button
                    key={tab}
                    type="button"
                    id={`supplier-tab-${tab}`}
                    role="tab"
                    aria-selected={activeTab === tab}
                    aria-controls={`supplier-tabpanel-${tab}`}
                    tabIndex={activeTab === tab ? 0 : -1}
                    onClick={() => setActiveTab(tab)}
                    onKeyDown={(event) => {
                      let nextIndex: number | null = null;
                      if (event.key === 'ArrowRight') nextIndex = (index + 1) % supplierDetailTabs.length;
                      if (event.key === 'ArrowLeft') nextIndex = (index - 1 + supplierDetailTabs.length) % supplierDetailTabs.length;
                      if (event.key === 'Home') nextIndex = 0;
                      if (event.key === 'End') nextIndex = supplierDetailTabs.length - 1;
                      if (nextIndex === null) return;
                      event.preventDefault();
                      const nextTab = supplierDetailTabs[nextIndex].value;
                      setActiveTab(nextTab);
                      requestAnimationFrame(() => document.getElementById(`supplier-tab-${nextTab}`)?.focus());
                    }}
                    className={`whitespace-nowrap border-b-2 px-3 py-3 text-sm font-medium ${activeTab === tab ? 'border-blue-600 text-blue-700' : 'border-transparent text-gray-500 hover:text-gray-800'}`}
                  >
                    {label}
                  </button>
                ))}
              </div>

              <div
                id={`supplier-tabpanel-${activeTab}`}
                role="tabpanel"
                aria-labelledby={`supplier-tab-${activeTab}`}
                tabIndex={0}
                className="p-4 focus:outline-none"
              >
                {activeTab === 'documentos' && (
                  <DocumentosTab
                    selected={selected}
                    documentTypes={documentTypes}
                    documentType={documentType}
                    setDocumentType={setDocumentType}
                    documentExpiresAt={documentExpiresAt}
                    setDocumentExpiresAt={setDocumentExpiresAt}
                    documentIssuedAt={documentIssuedAt}
                    setDocumentIssuedAt={setDocumentIssuedAt}
                    setDocumentFile={setDocumentFile}
                    documentFile={documentFile}
                    handleUploadDocument={handleUploadDocument}
                    uploading={addDocument.isPending}
                    reviewReason={reviewReason}
                    setReviewReason={setReviewReason}
                    onReview={handleReviewDocument}
                    reviewing={reviewDocument.isPending}
                  />
                )}
                {activeTab === 'homologacao' && (
                  <HomologacaoTab
                    selected={selected}
                    approvalStep={approvalStep}
                    setApprovalStep={(value) => {
                      setApprovalStep(value);
                      setApprovalValidityError(null);
                    }}
                    approvalDecision={approvalDecision}
                    setApprovalDecision={(value) => {
                      setApprovalDecision(value);
                      setApprovalValidityError(null);
                    }}
                    approvalJustification={approvalJustification}
                    setApprovalJustification={setApprovalJustification}
                    approvalValidUntil={approvalValidUntil}
                    setApprovalValidUntil={(value) => {
                      setApprovalValidUntil(value);
                      setApprovalValidityError(null);
                    }}
                    approvalValidityError={approvalValidityError}
                    onSubmit={handleApproval}
                    loading={addApproval.isPending}
                  />
                )}
                {activeTab === 'avaliacoes' && (
                  <AvaliacoesTab selected={selected} evaluation={evaluation} setEvaluation={setEvaluation} onSubmit={handleEvaluation} loading={addEvaluation.isPending} />
                )}
                {activeTab === 'portal' && <PortalTab selected={selected} />}
                {activeTab === 'historico' && <HistoricoTab selected={selected} />}
                {activeTab === 'integracoes' && <IntegracoesTab selected={selected} />}
              </div>
            </div>
          ) : (
            <div className="p-6 text-sm text-gray-500">
              {selectedSupplierId && isSelectedDetailFetching
                ? 'Atualizando fornecedor selecionado...'
                : 'Selecione um fornecedor para ver detalhes.'}
            </div>
          )}
        </aside>
      </div>
    </div>
  );
};

function MetricCard({ icon, label, value, tone = 'default', testId }: {
  icon: React.ReactNode;
  label: string;
  value: string | number;
  tone?: 'default' | 'danger';
  testId?: string;
}) {
  return (
    <div data-testid={testId} className="rounded-lg border border-gray-200 bg-white p-4 shadow-sm">
      <div className="flex items-center justify-between">
        <span className="text-sm text-gray-500">{label}</span>
        <span className={tone === 'danger' ? 'text-red-600' : 'text-blue-600'}>{React.cloneElement(icon as React.ReactElement, { className: 'h-5 w-5' })}</span>
      </div>
      <div data-testid={testId ? `${testId}-value` : undefined} className="mt-2 text-2xl font-bold text-gray-900">{value}</div>
    </div>
  );
}

function Input({
  field,
  label,
  value,
  onChange,
  type = 'text',
  required = false,
  error,
  inputMode,
  maxLength,
  min,
  max,
  disabled = false,
  placeholder,
}: {
  field: keyof CreateSupplierPayload;
  label: string;
  value: string;
  onChange: (value: string) => void;
  type?: string;
  required?: boolean;
  error?: string;
  inputMode?: React.HTMLAttributes<HTMLInputElement>['inputMode'];
  maxLength?: number;
  min?: number;
  max?: number;
  disabled?: boolean;
  placeholder?: string;
}) {
  const id = React.useId();
  const errorId = `${id}-error`;
  return (
    <label htmlFor={id} className="block">
      <span className="mb-1 block text-sm font-medium text-gray-700">{label}{required && <span className="text-red-500"> *</span>}</span>
      <input
        id={id}
        data-supplier-field={field}
        type={type}
        value={value}
        required={required}
        disabled={disabled}
        inputMode={inputMode}
        maxLength={maxLength}
        min={min}
        max={max}
        placeholder={placeholder}
        aria-invalid={Boolean(error)}
        aria-describedby={error ? errorId : undefined}
        onChange={(event) => onChange(event.target.value)}
        className={`w-full rounded-lg border px-3 py-2 text-sm focus:outline-none focus:ring-1 disabled:bg-gray-100 ${error ? 'border-red-400 focus:border-red-500 focus:ring-red-500' : 'border-gray-300 focus:border-blue-500 focus:ring-blue-500'}`}
      />
      {error && <span id={errorId} role="alert" className="mt-1 block text-xs text-red-600">{error}</span>}
    </label>
  );
}

function Select({ field, label, value, onChange, children, error, disabled = false }: {
  field: keyof CreateSupplierPayload;
  label: string;
  value: string;
  onChange: (value: string) => void;
  children: React.ReactNode;
  error?: string;
  disabled?: boolean;
}) {
  const id = React.useId();
  const errorId = `${id}-error`;
  return (
    <label htmlFor={id} className="block">
      <span className="mb-1 block text-sm font-medium text-gray-700">{label}</span>
      <select
        id={id}
        data-supplier-field={field}
        value={value}
        disabled={disabled}
        aria-invalid={Boolean(error)}
        aria-describedby={error ? errorId : undefined}
        onChange={(event) => onChange(event.target.value)}
        className={`w-full rounded-lg border px-3 py-2 text-sm focus:outline-none disabled:bg-gray-100 ${error ? 'border-red-400 focus:border-red-500' : 'border-gray-300 focus:border-blue-500'}`}
      >
        {children}
      </select>
      {error && <span id={errorId} role="alert" className="mt-1 block text-xs text-red-600">{error}</span>}
    </label>
  );
}

function Badge({ children, className }: { children: React.ReactNode; className: string }) {
  return <span className={`inline-flex items-center rounded-full px-2.5 py-1 text-xs font-semibold ${className}`}>{children}</span>;
}

function Th({ children }: { children: React.ReactNode }) {
  return <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wider text-gray-500">{children}</th>;
}

function SummaryMini({ label, value }: { label: string; value: string | number }) {
  return (
    <div className="rounded-lg bg-gray-50 px-2 py-2">
      <div className="text-xs text-gray-500">{label}</div>
      <div className="text-sm font-bold text-gray-900">{value}</div>
    </div>
  );
}

function DocumentosTab(props: {
  selected: Supplier;
  documentTypes: SupplierDocumentType[];
  documentType: string;
  setDocumentType: (value: string) => void;
  documentExpiresAt: string;
  setDocumentExpiresAt: (value: string) => void;
  documentIssuedAt: string;
  setDocumentIssuedAt: (value: string) => void;
  setDocumentFile: (file: File | null) => void;
  documentFile: File | null;
  handleUploadDocument: () => void;
  uploading: boolean;
  reviewReason: string;
  setReviewReason: (value: string) => void;
  onReview: (document: SupplierDocument, status: 'aprovado' | 'reprovado') => void;
  reviewing: boolean;
}) {
  const { selected } = props;
  const documentCodes = supplierDocumentCodes(
    selected.supplierType,
    selected.requiredDocuments,
    selected.documents,
    props.documentTypes,
  );
  const uploadDocumentTypes = supplierUploadDocumentTypes(
    selected.supplierType,
    selected.requiredDocuments,
    selected.documents,
    props.documentTypes,
  );
  return (
    <div className="space-y-4">
      <div id="supplier-document-upload-admin">
        <SupplierDocumentUpload
          documentTypes={uploadDocumentTypes}
          documentType={props.documentType}
          onDocumentTypeChange={props.setDocumentType}
          expiresAt={props.documentExpiresAt}
          onExpiresAtChange={props.setDocumentExpiresAt}
          issuedAt={props.documentIssuedAt}
          onIssuedAtChange={props.setDocumentIssuedAt}
          file={props.documentFile}
          onFileChange={props.setDocumentFile}
          onSubmit={props.handleUploadDocument}
          submitting={props.uploading}
          submitLabel="Enviar documento"
        />
      </div>

      <div>
        <h3 className="mb-2 text-sm font-semibold text-gray-900">Controle documental</h3>
        <div className="space-y-2">
          {documentCodes.map((type) => (
            <SupplierDocumentCard
              key={type}
              documentCode={type}
              documentTypes={props.documentTypes}
              documents={selected.documents}
              required={selected.requiredDocuments.includes(type)}
              onReview={props.onReview}
              reviewing={props.reviewing}
              onRenew={() => {
                props.setDocumentType(type);
                document.getElementById('supplier-document-upload-admin')?.scrollIntoView({ behavior: 'smooth' });
              }}
            />
          ))}
        </div>
        <label className="mt-3 block text-sm font-medium text-gray-700">
          Justificativa para reprovação de documento
          <textarea
            value={props.reviewReason}
            onChange={(event) => props.setReviewReason(event.target.value)}
            placeholder="Justificativa para reprovação de documento"
            className="mt-1 h-16 w-full rounded-lg border border-gray-300 px-3 py-2 text-sm"
          />
        </label>
      </div>
    </div>
  );
}

function HomologacaoTab(props: {
  selected: Supplier;
  approvalStep: HomologationStep;
  setApprovalStep: (value: HomologationStep) => void;
  approvalDecision: SupplierApprovalDecision;
  setApprovalDecision: (value: SupplierApprovalDecision) => void;
  approvalJustification: string;
  setApprovalJustification: (value: string) => void;
  approvalValidUntil: string;
  setApprovalValidUntil: (value: string) => void;
  approvalValidityError: string | null;
  onSubmit: () => void;
  loading: boolean;
}) {
  const validityErrorId = React.useId();
  const requiresValidity = props.approvalStep === 'aprovacao_final'
    && props.approvalDecision !== 'reprovado';
  return (
    <div className="space-y-4">
      <SupplierHomologationMetadata supplier={props.selected} />
      <div>
        <h3 className="mb-3 text-sm font-semibold text-gray-900">Workflow de homologação</h3>
        <div className="space-y-2">
          {workflowSteps.map((step, index) => {
            const latestApproval = latestApprovalByStep(props.selected, step.value);
            const done = latestApproval?.decision === 'aprovado'
              || latestApproval?.decision === 'aprovado_restricao';
            const active = props.selected.homologationStep === step.value;
            return (
              <div key={step.value} className={`flex items-center gap-3 rounded-lg border px-3 py-2 ${active ? 'border-blue-200 bg-blue-50' : 'border-gray-200 bg-white'}`}>
                <div className={`flex h-7 w-7 items-center justify-center rounded-full text-xs font-bold ${done ? 'bg-emerald-600 text-white' : active ? 'bg-blue-600 text-white' : 'bg-gray-100 text-gray-600'}`}>{index + 1}</div>
                <div className="text-sm font-medium text-gray-800">{step.label}</div>
              </div>
            );
          })}
        </div>
      </div>

      <div className="rounded-lg border border-gray-200 p-3">
        <div className="mb-3 text-sm font-semibold text-gray-900">Registrar decisão</div>
        <div className="grid gap-3">
          <label className="text-sm font-medium text-gray-700">
            Etapa da homologação
            <select value={props.approvalStep} onChange={(event) => props.setApprovalStep(event.target.value as HomologationStep)} className="mt-1 block w-full rounded-lg border border-gray-300 px-3 py-2 text-sm">
              {workflowSteps.map((step) => <option key={step.value} value={step.value}>{step.label}</option>)}
            </select>
          </label>
          <label className="text-sm font-medium text-gray-700">
            Decisão da homologação
            <select value={props.approvalDecision} onChange={(event) => props.setApprovalDecision(event.target.value as SupplierApprovalDecision)} className="mt-1 block w-full rounded-lg border border-gray-300 px-3 py-2 text-sm">
              <option value="aprovado">Aprovar</option>
              <option value="aprovado_restricao">Aprovar com restrição</option>
              <option value="reprovado">Reprovar</option>
            </select>
          </label>
          <label className="text-sm font-medium text-gray-700">
            Justificativa
            <textarea value={props.approvalJustification} onChange={(event) => props.setApprovalJustification(event.target.value)} placeholder="Justificativa" className="mt-1 h-20 w-full rounded-lg border border-gray-300 px-3 py-2 text-sm" />
          </label>
          {requiresValidity && (
            <label className="text-sm font-medium text-gray-700">
              Validade da homologação <span className="text-red-500">*</span>
              <input
                type="date"
                required
                min={nextCivilDateISO()}
                value={props.approvalValidUntil}
                onChange={(event) => props.setApprovalValidUntil(event.target.value)}
                aria-invalid={Boolean(props.approvalValidityError)}
                aria-describedby={props.approvalValidityError ? validityErrorId : undefined}
                className={`mt-1 block w-full rounded-lg border px-3 py-2 text-sm ${props.approvalValidityError ? 'border-red-400' : 'border-gray-300'}`}
              />
              {props.approvalValidityError && <span id={validityErrorId} role="alert" className="mt-1 block text-xs text-red-600">{props.approvalValidityError}</span>}
            </label>
          )}
          <button type="button" onClick={props.onSubmit} disabled={props.loading} className="rounded-lg bg-blue-600 px-4 py-2 text-sm font-semibold text-white hover:bg-blue-700 disabled:opacity-60">
            Registrar homologação
          </button>
        </div>
      </div>

      <div className="space-y-2">
        {props.selected.approvals.map((approval) => (
          <div key={approval.id} className="rounded-lg border border-gray-200 p-3 text-sm">
            <div className="font-medium text-gray-900">{stepLabels[approval.step]} · {approval.decision}</div>
            <div className="text-xs text-gray-500">{approval.approvedByName || 'Usuário'} · {formatLocalDate(approval.approvedAt)}</div>
            {approval.justification && <p className="mt-1 text-gray-700">{approval.justification}</p>}
          </div>
        ))}
      </div>
    </div>
  );
}

function AvaliacoesTab({ selected, evaluation, setEvaluation, onSubmit, loading }: {
  selected: Supplier;
  evaluation: { qualityScore: number; deadlineScore: number; serviceScore: number; complianceScore: number; priceScore: number; slaScore: number; comments: string };
  setEvaluation: React.Dispatch<React.SetStateAction<{ qualityScore: number; deadlineScore: number; serviceScore: number; complianceScore: number; priceScore: number; slaScore: number; comments: string }>>;
  onSubmit: () => void;
  loading: boolean;
}) {
  const fields = [
    ['qualityScore', 'Qualidade'],
    ['deadlineScore', 'Prazo'],
    ['serviceScore', 'Atendimento'],
    ['complianceScore', 'Conformidade'],
    ['priceScore', 'Preço'],
    ['slaScore', 'SLA'],
  ] as const;
  return (
    <div className="space-y-4">
      <div className="grid grid-cols-2 gap-3">
        {fields.map(([key, label]) => (
          <label key={key} className="text-sm font-medium text-gray-700">
            {label}
            <input type="number" min={1} max={5} value={evaluation[key]} onChange={(event) => setEvaluation((current) => ({ ...current, [key]: Number(event.target.value) }))} className="mt-1 w-full rounded-lg border border-gray-300 px-3 py-2 text-sm" />
          </label>
        ))}
      </div>
      <textarea value={evaluation.comments} onChange={(event) => setEvaluation((current) => ({ ...current, comments: event.target.value }))} placeholder="Comentários da avaliação" className="h-20 w-full rounded-lg border border-gray-300 px-3 py-2 text-sm" />
      <button type="button" onClick={onSubmit} disabled={loading} className="rounded-lg bg-blue-600 px-4 py-2 text-sm font-semibold text-white hover:bg-blue-700 disabled:opacity-60">Registrar avaliação</button>

      <div className="space-y-2">
        {selected.evaluations.map((item) => (
          <div key={item.id} className="rounded-lg border border-gray-200 p-3 text-sm">
            <div className="font-medium text-gray-900">Média {Number(item.averageScore).toFixed(1)} · {item.result}</div>
            <div className="text-xs text-gray-500">{formatLocalDate(item.createdAt)}</div>
            {item.comments && <p className="mt-1 text-gray-700">{item.comments}</p>}
          </div>
        ))}
      </div>
    </div>
  );
}

function PortalTab({ selected }: { selected: Supplier }) {
  return (
    <div className="space-y-3 text-sm">
      <SupplierBlockingConditionsPanel supplier={selected} title="Pendências visíveis ao fornecedor" showEligible />
      <SupplierHomologationMetadata supplier={selected} />
      <PortalItem icon={<UserRound />} title="Atualização cadastral" text={`${selected.contactName} pode revisar dados cadastrais, fiscais, bancários e contatos.`} />
      <PortalItem icon={<UploadCloud />} title="Envio de documentos" text={`${selected.pendingRequiredDocuments.length} documento(s) obrigatório(s) pendente(s) para homologação.`} />
      <PortalItem icon={<FileCheck2 />} title="Acompanhamento" text={`Status atual: ${statusLabels[selected.status]} · etapa ${stepLabels[selected.homologationStep]}.`} />
      <PortalItem icon={<Star />} title="Avaliações" text={`Avaliação média atual: ${Number(selected.averageRating || 0).toFixed(1)}.`} />
    </div>
  );
}

function HistoricoTab({ selected }: { selected: Supplier }) {
  return (
    <div className="space-y-2">
      {selected.history.map((item) => {
        const changedFields = item.action === 'supplier.updated'
          ? Object.keys(item.newValue ?? {}).map((field) => supplierAuditFieldLabels[field] ?? field)
          : [];
        return (
          <div key={item.id} className="flex gap-3 rounded-lg border border-gray-200 p-3 text-sm">
            <History className="mt-0.5 h-4 w-4 text-gray-400" />
            <div>
              <div className="font-medium text-gray-900">{item.action}</div>
              <div className="text-xs text-gray-500">{item.userName || 'Sistema'} · {formatLocalDate(item.createdAt)}</div>
              {changedFields.length > 0 && (
                <div className="mt-1 text-xs text-gray-600">Campos alterados: {changedFields.join(', ')}.</div>
              )}
            </div>
          </div>
        );
      })}
      {selected.history.length === 0 && <div className="text-sm text-gray-500">Sem histórico registrado.</div>}
    </div>
  );
}

function IntegracoesTab({ selected }: { selected: Supplier }) {
  const blockingConditions = getSupplierBlockingConditions(selected);
  const blockingDetails = blockingConditions.map((condition) => condition.message).join(' | ');
  const items = [
    ['Compras', selected.purchaseEligible
      ? 'Fornecedor liberado para vínculo a processos de compra.'
      : `Fornecedor bloqueado para compras. Causas: ${blockingDetails}`],
    ['Contratos', selected.purchaseEligible
      ? 'Fornecedor liberado para vínculo a contratos.'
      : `Fornecedor bloqueado para contratos. Causas: ${blockingDetails}`],
    ['ERP Financeiro', 'Dados bancários e fiscais disponíveis para integração futura.'],
    ['Power BI', 'Indicadores consolidados no dashboard gerencial.'],
  ];
  return (
    <div className="space-y-2">
      {items.map(([title, text]) => (
        <div key={title} className="flex gap-3 rounded-lg border border-gray-200 p-3 text-sm">
          <Link2 className="mt-0.5 h-4 w-4 text-blue-600" />
          <div>
            <div className="font-medium text-gray-900">{title}</div>
            <div className="text-gray-600">{text}</div>
          </div>
        </div>
      ))}
    </div>
  );
}

function PortalItem({ icon, title, text }: { icon: React.ReactNode; title: string; text: string }) {
  return (
    <div className="flex gap-3 rounded-lg border border-gray-200 p-3">
      <span className="mt-0.5 text-blue-600">{React.cloneElement(icon as React.ReactElement, { className: 'h-4 w-4' })}</span>
      <div>
        <div className="font-medium text-gray-900">{title}</div>
        <div className="text-gray-600">{text}</div>
      </div>
    </div>
  );
}

export default FornecedoresComprasPage;
